import React, { memo, ReactElement} from "react";
import cn from "classnames";
import { useFormikContext } from "formik";
import BaseCheckbox from "../../Checkboxes/BaseCheckbox";
import EmailField from "../../EmailField/EmailField";
import { handleOnChange, handleOnBlur } from "../DirectApplyUtils"
import { JobCategory, JobLocation } from "../DirectApplyTypes";
import { getScreenByStep, impressionBase } from "../constants";
import InlineDropDown from "../../Dropdowns/InlineDropDown";
import styles from "./Subscribe.module.scss";

interface OptionsMap {
    label: string,
    value: string;
};

/**
 * Generates the job alert text label.
 *
 * @returns {ReactElement} the content of the job alert label.
 */
const JobAlertText = memo((props: {
    category: JobCategory | null,
    options: OptionsMap[],
}): ReactElement => {
    const { category, options } = props;
    const { values, setFieldValue } = useFormikContext();
    const valueObj = values as Record<string, string>;
    const locationString = valueObj.location.includes("Remote") ? "that are " : "in ";

    return (
        <span>
            {"Get "}{!!category && <b>{category?.value}</b>} jobs
            {" "}{valueObj.location ? locationString : ""}
            {" "}{valueObj.location ?
                (<InlineDropDown {...{
                    options,
                    setExternalState: (x) => setFieldValue("location", x),
                    preselectedValue: valueObj.location,
                    maxItems: 5
                }} />) : null}
            {" "}delivered to your inbox every week.
        </span>
    );
});
JobAlertText.displayName = "JobAlertText";

/**
 * Renders a subscribe form containing the newsletter and jobAlerts selections along with email field.
 *
 * @returns {ReactElement} The confirm screen.
 */
const Subscribe = (props: {
    locations: JobLocation[],
    jobCategory: JobCategory | null,
    enabled: boolean;
}): ReactElement => {
    const { locations, jobCategory, enabled = false } = props;
    const jobLocationOptions: OptionsMap[] = locations.map((x) => ({ label: x.value, value: x.value }));

    const { errors, values, handleChange, setFieldTouched, setFieldValue, touched } = useFormikContext();
    // Convenient wrappers to get correct typings for these objects that have undefined keys.
    const errorObj = errors as Record<string, string>;
    const valueObj = values as Record<string, string>;
    const touchedObj = touched as Record<string, string>;
    const screen = getScreenByStep(1);
    const impression = {
        ...impressionBase,
        sp_modal_screen_name: screen.name,
        sp_modal_screen_order: screen.step,
    };

    /**
     * Updates the form state when the alerts checkbox is modified.
     */
    const onAlertsChange = () => {
        setFieldValue("alerts", valueObj.alerts === "true" ? "false" : "true");
    };

    /**
     * Updates the form state when the newsletter checkbox is modified.
     */
    const onNewsletterChange = () => {
        setFieldValue("newsletter", valueObj.newsletter === "true" ? "false" : "true");
    };

    return (
        <div className={cn(styles.subscribe, enabled ? styles.enabled : "")}
            data-tracking-props={JSON.stringify(impression)}
            data-tracking-event-type="impression"
        >
            <p className={styles.screenHeading}>Contact info</p>
            <EmailField
                className={styles.EmailFieldErrorOverride}
                {...{
                    "type": "email",
                    "name": "email",
                    "label": "Email",
                    "id": "email",
                    "required": true,
                    onBlur: handleOnBlur({handleChange, touchedObj, setFieldTouched}),
                    onChange: handleOnChange({ handleChange, touchedObj, setFieldTouched }),
                    defaultValue: valueObj.email  ?? "",
                    fieldErrors: touchedObj.email ? [errorObj?.email].filter(Boolean) : [],
                    labelClassName: styles.label,
                }}
            />
            <div className={styles.checkBoxLabels}>
                <BaseCheckbox {...{
                    checked: valueObj.alerts === "true",
                    labelText: <JobAlertText {...{
                        category: jobCategory,
                        options: jobLocationOptions,
                        setFieldValue
                    }} />,
                    inputId: "alerts",
                    onChange: onAlertsChange,
                    name: "JobAlert"
                }} />

            </div>
            <div className={styles.checkBoxLabels}>
                <BaseCheckbox {...{
                    checked: valueObj.newsletter === "true",
                    labelText: "Send me The Muse newsletters for the best in career advice and job search tips.",
                    inputId: "newsletters",
                    onChange: onNewsletterChange
                }} />
            </div>
            <p className={styles.tos}>
                By entering your email, you agree to our <a href="/user/terms" target="_blank">Terms of Service</a>
                {" & "}
                <a href="/user/privacy" target="_blank">Privacy Policy</a>.
            </p>
        </div>
    );
};
Subscribe.displayName = "Subscribe";

export default Subscribe;
